import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Home, DollarSign, BookOpen, LogIn } from 'lucide-react';
import LoadingScreen from '@/components/LoadingScreen';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIZE_CLASSES = {
  sm: 'md:col-span-1 md:row-span-1',
  md: 'md:col-span-2 md:row-span-1',
  lg: 'md:col-span-2 md:row-span-2',
  tall: 'md:col-span-1 md:row-span-2',
  wide: 'md:col-span-3 md:row-span-1',
};

export default function HowItWorks() {
  const [s, setS] = useState({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/site-settings`).then(r => { setS(r.data); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);

  // Show loading splash while we don't yet know whether the page is enabled / what content to show
  if (!loaded) return <LoadingScreen />;

  const items = Array.isArray(s.how_it_works_items) ? s.how_it_works_items : [];
  const headerLogoSize = s.header_logo_size || 36;
  const disabled = loaded && s.how_it_works_enabled === false;

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-orange-50 pb-24 md:pb-0">
      {/* Header */}
      <header className="border-b border-stone-200 bg-white/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            {s.site_logo_path ? <SiteLogo path={s.site_logo_path} size={headerLogoSize} /> :
              <img src="/olafel_logo.png" alt="Olafel" style={{ width: `${headerLogoSize}px`, height: `${headerLogoSize}px` }} className="object-contain" />}
            <span className="font-bold text-lg text-stone-900" style={{ fontFamily: 'Outfit' }}>{s.site_name || 'Call Button'}</span>
          </Link>
          {/* Desktop nav links */}
          <nav className="hidden md:flex items-center gap-5">
            <Link to="/#pricing" className="text-sm text-stone-600 hover:text-orange-700 font-medium">Pricing</Link>
            <Link to="/how-it-works" className="text-sm text-orange-700 font-semibold">How It Works</Link>
            <Link to="/login" className="text-sm text-stone-600 hover:text-orange-700 font-medium">Sign In</Link>
            <Link to="/register">
              <Button className="bg-orange-600 hover:bg-orange-700 text-white" size="sm">Get Started</Button>
            </Link>
          </nav>
          {/* Mobile single CTA */}
          <Link to="/register" className="md:hidden">
            <Button className="bg-orange-600 hover:bg-orange-700 text-white" size="sm">Get Started</Button>
          </Link>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-100 text-orange-700 text-xs font-bold uppercase tracking-wider mb-4">
            How it works
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>
            From scan to served —<br /> in seconds
          </h1>
          <p className="text-base text-stone-500 mt-4 max-w-xl mx-auto">
            A simple flow that turns tables into connected service points.
          </p>
        </div>

        {disabled ? (
          <div className="py-20 text-center text-stone-400 text-sm">This page is currently unavailable.</div>
        ) : items.length === 0 ? (
          <div className="py-20 text-center text-stone-400 text-sm">Content coming soon.</div>
        ) : (
          <>
            {/* Desktop/tablet: bento grid */}
            <div className="hidden md:grid grid-cols-4 gap-5 auto-rows-[280px]">
              {items.map(item => (
                <BentoCard key={item.id} item={item} />
              ))}
            </div>
            {/* Mobile: stacked */}
            <div className="md:hidden space-y-4">
              {items.map(item => (
                <BentoCard key={item.id} item={item} mobile />
              ))}
            </div>
          </>
        )}
      </section>

      <footer className="border-t border-stone-200 bg-white mt-12">
        <div className="max-w-6xl mx-auto px-6 py-6 text-center text-xs text-stone-400">
          © {new Date().getFullYear()} {s.site_name || 'Call Button'} · All rights reserved.
        </div>
      </footer>

      {/* Mobile bottom navigation */}
      <nav
        data-testid="public-mobile-bottom-nav"
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-t border-stone-200 safe-bottom"
      >
        <div className="grid grid-cols-4">
          <Link to="/" data-testid="mobile-nav-home" className="flex flex-col items-center justify-center gap-0.5 py-2.5 text-stone-600 active:text-orange-600 active:scale-95 transition-all">
            <Home size={20} />
            <span className="text-[10px] font-medium">Home</span>
          </Link>
          <Link to="/#pricing" data-testid="mobile-nav-pricing" className="flex flex-col items-center justify-center gap-0.5 py-2.5 text-stone-600 active:text-orange-600 active:scale-95 transition-all">
            <DollarSign size={20} />
            <span className="text-[10px] font-medium">Pricing</span>
          </Link>
          <Link to="/how-it-works" data-testid="mobile-nav-how" className="flex flex-col items-center justify-center gap-0.5 py-2.5 text-orange-600 transition-all">
            <BookOpen size={20} />
            <span className="text-[10px] font-medium">How</span>
          </Link>
          <Link to="/login" data-testid="mobile-nav-signin" className="flex flex-col items-center justify-center gap-0.5 py-2.5 text-stone-600 active:text-orange-600 active:scale-95 transition-all">
            <LogIn size={20} />
            <span className="text-[10px] font-medium">Sign In</span>
          </Link>
        </div>
      </nav>
    </div>
  );
}

function BentoCard({ item, mobile }) {
  const sizeClass = mobile ? '' : (SIZE_CLASSES[item.size] || SIZE_CLASSES.sm);
  const bgColor = item.overlay_color || '#1c1917';
  const opacity = typeof item.overlay_opacity === 'number' ? item.overlay_opacity : 100;
  const textColor = item.text_color || '#ffffff';
  const imageFit = item.image_fit === 'contain' ? 'contain' : 'cover';
  const hasImage = !!item.image_path;
  const hasText = !!(item.title || item.description);
  const hex = bgColor.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16) || 0;
  const g = parseInt(hex.slice(2, 4), 16) || 0;
  const b = parseInt(hex.slice(4, 6), 16) || 0;
  const a = Math.max(0, Math.min(100, opacity)) / 100;
  const textBg = `rgba(${r},${g},${b},${a})`;
  // Softer background behind the image when using "contain" so letterboxing looks intentional
  const imageBg = imageFit === 'contain' ? '#f5f5f4' : '#111827';
  return (
    <div className={`relative rounded-2xl overflow-hidden bg-white border border-stone-200 shadow-sm hover:shadow-lg transition-shadow group flex flex-col ${sizeClass} ${mobile ? 'h-72' : ''}`}>
      {/* Image — takes the available space above the text box */}
      {hasImage && (
        <div className="relative flex-1 min-h-0" style={{ background: imageBg }}>
          <BentoImage path={item.image_path} fit={imageFit} />
        </div>
      )}
      {/* Text sits in its own box, solid background, no overlap with image */}
      {hasText && (
        <div className={`px-4 py-3 ${hasImage ? 'shrink-0' : 'flex-1 flex flex-col justify-center'}`} style={{ background: textBg, color: textColor }}>
          {item.title && <h3 className="font-bold text-base leading-tight mb-1" style={{ fontFamily: 'Outfit' }}>{item.title}</h3>}
          {item.description && <p className="text-xs leading-relaxed opacity-95">{item.description}</p>}
        </div>
      )}
      {/* If no text and no image, show a subtle placeholder */}
      {!hasImage && !hasText && <div className="flex-1 bg-gradient-to-br from-orange-100 to-amber-100" />}
    </div>
  );
}

function BentoImage({ path, fit = 'cover' }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!path) return;
    let revoke;
    axios.get(`${API}/files/${path}`, { responseType: 'blob' })
      .then(r => { const url = URL.createObjectURL(r.data); revoke = url; setSrc(url); })
      .catch(() => {});
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [path]);
  if (!src) return <div className="absolute inset-0 bg-gradient-to-br from-orange-200 to-amber-300" />;
  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      className={`absolute inset-0 w-full h-full transition-transform duration-500 ${fit === 'contain' ? 'object-contain' : 'object-cover group-hover:scale-[1.03]'}`}
      style={{ imageRendering: 'auto' }}
    />
  );
}

function SiteLogo({ path, size }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!path) return;
    let revoke;
    axios.get(`${API}/files/${path}`, { responseType: 'blob' })
      .then(r => { const url = URL.createObjectURL(r.data); revoke = url; setSrc(url); })
      .catch(() => {});
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [path]);
  if (!src) return <img src="/olafel_logo.png" alt="Olafel" style={{ width: `${size}px`, height: `${size}px` }} className="object-contain" />;
  return <img src={src} alt="Site logo" className="rounded-lg object-cover" style={{ width: `${size}px`, height: `${size}px` }} />;
}
